import React from 'react'
import './Button.css'

export type ButtonVariant = 'primary' | 'secondary' | 'icon'
export type ButtonSize = 'small' | 'medium' | 'large'

export interface ButtonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  disabled?: boolean
  active?: boolean
  children: React.ReactNode
  onClick?: () => void
  ariaLabel?: string
  className?: string
}

/**
 * Button Component
 *
 * Reusable button component following design guidelines
 *
 * Variants:
 * - primary: Yellow background, for primary CTAs
 * - secondary: Blue background, for secondary actions
 * - icon: Circle button, for icon-only actions
 *
 * States:
 * - active: For selected/toggled state
 * - disabled: For disabled state
 *
 * @component
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  disabled = false,
  active = false,
  children,
  onClick,
  ariaLabel,
  className = '',
}) => {
  const classNames = [
    'button',
    `button--${variant}`,
    `button--${size}`,
    active && 'button--active',
    disabled && 'button--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      className={classNames}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={active}
    >
      {children}
    </button>
  )
}
